import { QUESTION_REVIEW_TASK_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { WorkflowStepCode } from '@exam-management/graphql';
import { QuestionReviewStatusTag } from '@exam-management/task-management';
import { Flex, PaperSection, ReadonlyField } from '@shared/components';
import { useMemo, useState } from 'react';

import {
  QuestionReviewReviewerCard,
  QuestionReviewReviewerEmptyState,
  QUESTION_REVIEW_BORDER_COLOR,
} from '../../components';
import {
  ChangeReviewerModal,
  EditDueDateModal,
  RemoveReviewerModal,
  type SelectedReviewer,
} from '../../containers';
import { useQuestionReviewTaskDetails } from '../../providers';
import { sortByTaskCode } from '../../utils';
import { AddReviewerButton } from '../add-reviewer-button';
import { QuestionReviewSubtaskActionMenu } from '../question-review-subtask-action-menu';

const REVIEWER_STATUS_MAPPER = {
  [WorkflowStepCode.QuestionReviewTodo]: 'todo',
  [WorkflowStepCode.QuestionReviewInReview]: 'in-review',
  [WorkflowStepCode.QuestionReviewReviewed]: 'reviewed',
  [WorkflowStepCode.QuestionReviewDone]: 'done',
} as const;

type ReviewerDialogType = 'change-reviewer' | 'remove-reviewer' | 'edit-due-date' | null;

export default function QuestionReviewReviewerSection() {
  const { taskDetails } = useQuestionReviewTaskDetails();
  const { subtasks } = taskDetails;

  const [selectedReviewer, setSelectedReviewer] = useState<SelectedReviewer | null>(null);
  const [dialogType, setDialogType] = useState<ReviewerDialogType>(null);

  const excludedReviewerIds = useMemo(
    () => subtasks.map((subtask) => subtask.assigneeId).filter(Boolean) as string[],
    [subtasks],
  );

  const handleOpenDialog = (type: NonNullable<ReviewerDialogType>, reviewer: SelectedReviewer) => {
    setSelectedReviewer(reviewer);
    setDialogType(type);
  };

  const handleCloseDialog = () => {
    setDialogType(null);
    setSelectedReviewer(null);
  };

  const reviewers = useMemo(() => {
    return [...subtasks]
      .sort((a, b) => sortByTaskCode(a.code, b.code))
      .map((subtask, index) => {
        const status =
          REVIEWER_STATUS_MAPPER[
            subtask.currentWorkflowStep.code as keyof typeof REVIEWER_STATUS_MAPPER
          ];

        const reviewer: SelectedReviewer = {
          id: subtask.id,
          name: subtask.assignee
            ? `${subtask.assignee.firstname} ${subtask.assignee.lastname}`
            : '',
          dueDate: subtask.dueDate ?? taskDetails.dueDate,
          description: subtask.description,
          assigneeId: subtask.assigneeId ?? '',
        };

        return {
          id: subtask.id,
          order: index + 1,
          name: reviewer.name,
          taskCode: subtask.code,
          status,
          dueDate: subtask.dueDate,
          description: subtask.description,
          endAdornment: (
            <>
              <QuestionReviewStatusTag
                status={status}
                data-testid={QUESTION_REVIEW_TASK_DATA_TEST_ID.REVIEWER_CARD_STATUS_TAG}
              />
              <QuestionReviewSubtaskActionMenu
                workflowStepCode={subtask.currentWorkflowStep.code}
                onRemoveReviewer={() => handleOpenDialog('remove-reviewer', reviewer)}
                onChangeReviewer={() => handleOpenDialog('change-reviewer', reviewer)}
                onEditDueDate={() => handleOpenDialog('edit-due-date', reviewer)}
              />
            </>
          ),
        };
      });
  }, [subtasks, taskDetails.dueDate]);

  return (
    <>
      <PaperSection title='Reviewer' endAdornment={<AddReviewerButton />}>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            p: theme.spacing(6),
            gap: theme.spacing(4),
          })}
        >
          {reviewers.length > 0 ? (
            reviewers.map(({ description, status, ...reviewer }) => (
              <QuestionReviewReviewerCard
                {...reviewer}
                key={reviewer.id}
                color={QUESTION_REVIEW_BORDER_COLOR[status]}
                endAdornment={reviewer.endAdornment}
              >
                <ReadonlyField label='รายละเอียดเพิ่มเติม' content={description ?? '-'} />
              </QuestionReviewReviewerCard>
            ))
          ) : (
            <QuestionReviewReviewerEmptyState />
          )}
        </Flex>
      </PaperSection>

      <ChangeReviewerModal
        open={dialogType === 'change-reviewer'}
        reviewer={selectedReviewer}
        excludedReviewerIds={excludedReviewerIds}
        onClose={handleCloseDialog}
      />

      <RemoveReviewerModal
        open={dialogType === 'remove-reviewer'}
        reviewer={selectedReviewer}
        onClose={handleCloseDialog}
      />

      <EditDueDateModal
        open={dialogType === 'edit-due-date'}
        reviewer={selectedReviewer}
        onClose={handleCloseDialog}
      />
    </>
  );
}
